import React, { useState, useEffect } from "react";
import Icon from "../../../../components/AppIcon";

const IntegrationConfigModal = ({
  provider,
  existingSettings,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    integrationProviderId: provider.providerId,
    isEnabled: true,
    apiKey: "",
    baseUrl: "",
    username: "",
    password: "",
    additionalConfiguration: "",
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (existingSettings) {
      setFormData((prev) => ({
        ...prev,
        isEnabled: existingSettings.isEnabled ?? true,
      }));
    }
  }, [existingSettings]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Provider-specific validation
    if (
      provider.providerCode === "Sona" ||
      provider.providerCode === "SonaPOS" ||
      provider.providerCode === "Sona_HR" ||
      provider.providerCode === "Sona_Till"
    ) {
      if (!formData.apiKey) {
        newErrors.apiKey = "API Key is required for Sona";
      }
    }

    if (provider.providerCode === "Lightspeed" || provider.providerCode === "Lightspeed_Till") {
      if (!formData.username) {
        newErrors.username = "Username is required for Lightspeed";
      }
      if (!formData.password && !existingSettings) {
        newErrors.password = "Password is required for Lightspeed";
      }
    }

    if (provider.providerCode === "BambooHR") {
      if (!formData.apiKey) {
        newErrors.apiKey = "API Key is required for BambooHR";
      }
      if (!formData.baseUrl) {
        newErrors.baseUrl = "Subdomain URL is required for BambooHR";
      }
    }

    if (provider.providerCode === "RocketOS_HR" || provider.providerCode === "RocketOS_Till") {
      if (!formData.apiKey) {
        newErrors.apiKey = "API Key is required for RocketOS";
      }
      if (!formData.baseUrl) {
        newErrors.baseUrl = "Base URL is required for RocketOS — each client has their own domain";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  // Provider-specific configuration UI
  const renderProviderConfig = () => {
    const code = provider.providerCode;

    // RocketOS HR / RocketOS Till
    if (code === "RocketOS_HR" || code === "RocketOS_Till") {
      return (
        <>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              API Key <span className="text-error">*</span>
            </label>
            <input
              type="text"
              value={formData.apiKey}
              onChange={(e) => handleChange("apiKey", e.target.value)}
              className={`input w-full font-mono ${errors.apiKey ? "input-error" : ""}`}
              placeholder="Enter your RocketOS API key (Bearer token)"
            />
            {errors.apiKey && (
              <p className="text-error text-sm mt-1">{errors.apiKey}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Static bearer token provided by your RocketOS administrator
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Base URL <span className="text-error">*</span>
            </label>
            <input
              type="url"
              value={formData.baseUrl}
              onChange={(e) => handleChange("baseUrl", e.target.value)}
              className={`input w-full ${errors.baseUrl ? "input-error" : ""}`}
              placeholder="https://yourcompany.vela-erp.com/api/integration"
            />
            {errors.baseUrl && (
              <p className="text-error text-sm mt-1">{errors.baseUrl}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Your RocketOS domain — each client has their own instance
            </p>
          </div>
        </>
      );
    }

    // Sona HR / Sona POS
    if (code === "Sona" || code === "SonaPOS" || code === "Sona_HR" || code === "Sona_Till") {
      return (
        <>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              API Key <span className="text-error">*</span>
            </label>
            <input
              type="text"
              value={formData.apiKey}
              onChange={(e) => handleChange("apiKey", e.target.value)}
              className={`input w-full font-mono ${errors.apiKey ? "input-error" : ""}`}
              placeholder="Enter your Sona API key"
            />
            {errors.apiKey && (
              <p className="text-error text-sm mt-1">{errors.apiKey}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Custom Base URL{" "}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </label>
            <input
              type="url"
              value={formData.baseUrl}
              onChange={(e) => handleChange("baseUrl", e.target.value)}
              className="input w-full"
              placeholder="https://your-company.sona.io/api"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave blank to use the default Sona API endpoint
            </p>
          </div>
        </>
      );
    }

    // Lightspeed
    if (code === "Lightspeed") {
      return (
        <>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Username <span className="text-error">*</span>
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => handleChange("username", e.target.value)}
              className={`input w-full ${errors.username ? "input-error" : ""}`}
              placeholder="Enter your Lightspeed username"
            />
            {errors.username && (
              <p className="text-error text-sm mt-1">{errors.username}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Password <span className="text-error">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                className={`input w-full pr-10 ${errors.password ? "input-error" : ""}`}
                placeholder={
                  existingSettings ? "��������" : "Enter your password"
                }
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
              </button>
            </div>
            {existingSettings && (
              <p className="text-xs text-muted-foreground mt-1">
                Leave blank to keep existing password
              </p>
            )}
            {errors.password && (
              <p className="text-error text-sm mt-1">{errors.password}</p>
            )}
          </div>
        </>
      );
    }

    // BambooHR
    if (code === "BambooHR") {
      return (
        <>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              API Key <span className="text-error">*</span>
            </label>
            <input
              type="text"
              value={formData.apiKey}
              onChange={(e) => handleChange("apiKey", e.target.value)}
              className={`input w-full font-mono ${errors.apiKey ? "input-error" : ""}`}
              placeholder="Enter your BambooHR API key"
            />
            {errors.apiKey && (
              <p className="text-error text-sm mt-1">{errors.apiKey}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Subdomain <span className="text-error">*</span>
            </label>
            <div className="flex items-center">
              <span className="text-muted-foreground mr-1">https://</span>
              <input
                type="text"
                value={formData.baseUrl}
                onChange={(e) => handleChange("baseUrl", e.target.value)}
                className={`input flex-1 ${errors.baseUrl ? "input-error" : ""}`}
                placeholder="your-company"
              />
              <span className="text-muted-foreground ml-1">.bamboohr.com</span>
            </div>
            {errors.baseUrl && (
              <p className="text-error text-sm mt-1">{errors.baseUrl}</p>
            )}
          </div>
        </>
      );
    }

    // Rocket (internal - minimal config)
    if (code === "RocketHR" || code === "RocketPOS") {
      return (
        <div className="p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2 text-success">
            <Icon name="CheckCircle" size={20} />
            <span className="font-medium">No configuration required</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Rocket integrations are automatically configured for your tenant.
            Just enable the integration to start syncing data.
          </p>
        </div>
      );
    }

    // Generic fallback
    return (
      <>
        <div>
          <label className="block text-sm font-medium mb-1.5">API Key</label>
          <input
            type="text"
            value={formData.apiKey}
            onChange={(e) => handleChange("apiKey", e.target.value)}
            className="input w-full font-mono"
            placeholder="Enter API key"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">Base URL</label>
          <input
            type="url"
            value={formData.baseUrl}
            onChange={(e) => handleChange("baseUrl", e.target.value)}
            className="input w-full"
            placeholder="https://api.example.com"
          />
        </div>
      </>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-card rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              {provider.logoUrl ? (
                <img
                  src={provider.logoUrl}
                  alt={provider.providerName}
                  className="w-6 h-6 object-contain"
                />
              ) : (
                <Icon
                  name="Settings"
                  size={20}
                  className="text-muted-foreground"
                />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                Configure {provider.providerName}
              </h2>
              <p className="text-xs text-muted-foreground">
                {provider.providerCode}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]"
        >
          <div className="space-y-4">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div>
                <label className="font-medium">Enable Integration</label>
                <p className="text-sm text-muted-foreground">
                  Allow data sync with this provider
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isEnabled}
                  onChange={(e) => handleChange("isEnabled", e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            {/* Provider-specific config */}
            {renderProviderConfig()}

            {/* Additional Configuration (JSON) */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Additional Configuration{" "}
                <span className="text-muted-foreground font-normal">
                  (JSON)
                </span>
              </label>
              <textarea
                value={formData.additionalConfiguration}
                onChange={(e) =>
                  handleChange("additionalConfiguration", e.target.value)
                }
                className="input w-full min-h-[80px] font-mono text-sm"
                placeholder='{"store_id": "123", "sync_interval": "hourly"}'
              />
              <p className="text-xs text-muted-foreground mt-1">
                Optional advanced settings in JSON format
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="Check" size={16} />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IntegrationConfigModal;
